import { getAccessToken, getCurrentUser } from './authService'
import { withApiBaseUrl } from '../lib/apiBaseUrl'

export type AmbHistoryMode = 'settled' | 'pending' | 'all'

export interface AmbHistoryTransaction {
  _id: string
  transactionId: string
  betId?: string
  roundId?: string
  username: string
  originalUsername?: string
  displayUsername?: string
  vendorUsername?: string | null
  productId: string
  gameCode?: string
  gameName?: string | null
  transactionType: string
  status: string
  amount: number
  winAmount?: number
  netAmount?: number
  currency?: string
  balanceBefore?: number
  balanceAfter?: number
  placedAt?: string
  settledAt?: string
  cancelledAt?: string
  createdAt?: string
  updatedAt?: string
}

export interface AmbHistoryRoundEvent {
  _id: string
  transactionId: string
  betId?: string | null
  roundId?: string | null
  transactionType: string
  status: string
  amount: number
  winAmount?: number
  netAmount?: number
  balanceBefore?: number
  balanceAfter?: number
  createdAt?: string
  updatedAt?: string
  placedAt?: string
  settledAt?: string
  cancelledAt?: string
  gameCode?: string | null
  gameName?: string | null
  isFeature?: boolean
  isSingleState?: boolean
  isEndRound?: boolean
  payoutAmount?: number
  payloadBetAmount?: number
}

export interface AmbHistoryRoundSummary {
  _id: string
  roundId: string
  transactionId: string
  betId?: string | null
  username: string
  originalUsername?: string
  displayUsername?: string
  vendorUsername?: string | null
  productId: string
  gameCode?: string | null
  gameName?: string | null
  transactionType: 'ROUND_SUMMARY'
  status: string
  amount: number
  winAmount?: number
  netAmount?: number
  currency?: string
  balanceBefore?: number
  balanceAfter?: number
  placedAt?: string | null
  settledAt?: string | null
  cancelledAt?: string | null
  createdAt?: string | null
  updatedAt?: string | null
  eventCount: number
  transactionIds: string[]
  betIds: string[]
  statuses: string[]
  transactionTypes: string[]
  hasFeatureEvent?: boolean
  hasSingleStateEvent?: boolean
  zeroBetWinEventCount?: number
  events: AmbHistoryRoundEvent[]
}

export interface AmbHistoryPagination {
  total: number
  page: number
  limit: number
  pages: number
  hasNext: boolean
  hasPrev: boolean
}

export interface AmbHistoryResponse {
  username: string
  mode: AmbHistoryMode
  roundSummaries: AmbHistoryRoundSummary[]
  pagination: AmbHistoryPagination
}

export interface GetMyAmbTransactionsOptions {
  mode?: AmbHistoryMode
  page?: number
  limit?: number
  status?: string
  productId?: string
  transactionType?: string
  gameCode?: string
  gameName?: string
  startDate?: string
  endDate?: string
  sortBy?: string
  sortOrder?: 'asc' | 'desc'
  signal?: AbortSignal
}

export async function getMyAmbTransactions(
  options: GetMyAmbTransactionsOptions = {},
): Promise<AmbHistoryResponse> {
  const token = getAccessToken()
  const user = getCurrentUser()
  const tenantId = user?.tenantId || ''

  if (!token) {
    throw new Error('กรุณาเข้าสู่ระบบ')
  }

  if (!tenantId) {
    throw new Error('ไม่พบ tenant สำหรับโหลดประวัติการเล่น')
  }

  const { signal, ...queryOptions } = options

  const params = new URLSearchParams()
  Object.entries(queryOptions).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return
    params.set(key, String(value))
  })

  const response = await fetch(withApiBaseUrl(`/api/v1/amb/audit/me/round-summaries?${params.toString()}`), {
    cache: 'no-store',
    signal,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      'X-Tenant-ID': tenantId,
      'Cache-Control': 'no-cache',
      Pragma: 'no-cache',
    },
  })

  if (!response.ok) {
    const errorText = await response.text().catch(() => '')
    throw new Error(`HTTP ${response.status}: ${errorText}`)
  }

  const result = await response.json() as { success?: boolean; data?: AmbHistoryResponse; message?: string }
  if (!result.success || !result.data) {
    throw new Error(result.message || 'โหลดประวัติการเล่นไม่สำเร็จ')
  }

  return result.data
}
